import {
  getApiBaseUrl,
  getDefaultAuthHeaders,
} from "@/shared/services/api-client";
import { withCorrelationId } from "@/shared/services/correlation";
import {
  ensureAccessTokenFreshForApiCall,
  forceRefreshAccessTokenAfterUnauthorized,
} from "@/shared/services/keycloak";

/** Authenticated-user payload from `/auth/user/`. We only need the permissions map. */
export interface UserWithPermissions {
  /** Django permission map: `{ "<app>.<codename>": true|false }`. Missing key = not granted. */
  permissions: Record<string, boolean>;
}

/**
 * Auth endpoints are UNVERSIONED, but the API base ends in `/v1`. Strip it so
 * the path isn't double-versioned (`…/v1/auth/user/` would 404).
 */
function authBaseUrl(): string {
  return getApiBaseUrl().replace(/\/v1$/, "");
}

/**
 * GET `/auth/user/` — the authenticated user including the granular Django
 * permissions map. Sends the Keycloak bearer token via the shared auth headers.
 * Throws on a non-OK response (the caller fails-closed to an empty map).
 *
 * This endpoint is unversioned, so it can't go through the shared `apiGet`
 * (which prepends `/v1`); instead it mirrors that client's auth flow directly:
 * proactively refresh a stale/near-expired token before the call, and retry
 * once after a 401 → refresh. Without this, a transient stale-token 401 would
 * fail-closed in AuthProvider and hide every gated page until the next reload.
 */
export async function fetchAuthUser(
  signal?: AbortSignal,
): Promise<UserWithPermissions> {
  const url = `${authBaseUrl()}/auth/user/`;
  // This unversioned endpoint bypasses the shared api-client, so attach the
  // correlation id here too (fresh per attempt) — otherwise this call, and its
  // 401→refresh retry, would be untraceable in the backend logs.
  const requestOnce = () =>
    fetch(url, {
      method: "GET",
      // Fresh correlation id per attempt (getDefaultAuthHeaders carries none, so
      // withCorrelationId always generates one) — same tracing as the shared
      // api-client, via the single shared helper.
      headers: withCorrelationId(getDefaultAuthHeaders()),
      signal,
    });

  await ensureAccessTokenFreshForApiCall();
  let res = await requestOnce();
  if (res.status === 401) {
    const refreshed = await forceRefreshAccessTokenAfterUnauthorized();
    if (refreshed) {
      // getDefaultAuthHeaders() now reads the refreshed token.
      res = await requestOnce();
    }
  }
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}: ${res.statusText}`);
  }
  const data = (await res.json()) as Partial<UserWithPermissions> | null;
  return { permissions: data?.permissions ?? {} };
}
